import type { Person } from '@/store/people/people.types';
import { PaymentStatus, type Subscription } from '@/store/subscriptions/subscriptions.types';
import type { Vessel } from '@/store/vessels/vessels.types';

import { isLifetimePlan, isPaidPlan, timestamp } from './subscription.display';

/**
 * Taking a plan over from whoever is paying for it.
 *
 * A vessel's plan is not necessarily its owner's: a captain can buy one, and keeps paying for
 * it after an owner comes aboard — the People service leaves that plan running exactly as it
 * is (`ownerJoinedPaidVessel`). The owner takes it over by buying the plan themselves, which
 * is the same `POST /subscriptions/checkout` a vessel with no plan at all uses. The server
 * recognises it from the row rather than from anything in the body — `purchase.isTakeover`
 * (`internal/app/payments/captain.go`) is a live paid row whose `person_id` is somebody else
 * while the caller is the vessel's owner — and treats it as a purchase rather than a conflict.
 * The captain's subscription is untouched until the owner's payment clears, and the time it
 * had left comes across as free days on the new one.
 *
 * The same three facts decide what this app offers, because getting it wrong is not a refusal
 * but a wrong charge: `PATCH /subscriptions/{id}` is gated on `EditSubscription` alone and
 * checks no payer, so an owner "changing the plan" on a captain-paid vessel would re-price the
 * captain's subscription and bill their card.
 */

/**
 * Whether this person is the vessel's owner — the half of the takeover rule that is about the
 * boat rather than the bill. A vessel with no owner has nobody who could take a plan over.
 */
export const isVesselOwner = (vessel: Vessel, person: Person | undefined): boolean =>
  !!person && !!vessel.ownerId && vessel.ownerId === person.id;

/**
 * The facts a plan control needs about the caller's standing on one vessel. Structural rather
 * than the whole `ManagedVesselSubscription`, so this module stays under the hook that builds
 * one rather than beside it.
 */
type PlanParty = {
  subscription: Subscription | null;
  /** Whether the plan is billed to the caller — see `getBilledServiceIds`. */
  isPayer: boolean;
  isOwner: boolean;
  canEdit: boolean;
};

/**
 * The plan is real, and it is on somebody else's card. True for anyone looking at such a
 * vessel, owner or not: it is a statement about the bill, not a right. A lifetime plan is on
 * nobody's card, so it is never paid by another — and never taken over.
 */
export const isPaidByAnother = ({
  subscription,
  isPayer,
}: Pick<PlanParty, 'subscription' | 'isPayer'>): boolean =>
  isPaidPlan(subscription) && !isLifetimePlan(subscription) && !isPayer;

/**
 * Whether this caller may buy a plan out from under its current payer — the client-side read
 * of `purchase.isTakeover`, plus the `EditSubscription` right the endpoint is gated on.
 *
 * Anyone else looking at a plan they do not pay for — a captain on an owner-paid vessel, say —
 * is not offered it: the server would answer `ErrSubscriptionExists`, since a checkout for a
 * vessel that already has a live paid plan is a conflict for everybody but its owner.
 */
export const canTakeOverPlan = (party: PlanParty): boolean =>
  party.canEdit && party.isOwner && isPaidByAnother(party);

// ^ ------------------------- Time carried over -------------------------

/**
 * The shortest stretch of time a takeover can carry across.
 *
 * Carrying the old plan's remaining time over is done by starting the new subscription on a
 * Stripe trial that ends when the old one would have — and Stripe will not accept a
 * `trial_end` less than 48 hours out. The People service checks that before it asks
 * (`IsValidTrialEnd` against `MinTrialPeriod`, `internal/pkg/stripe/helpers.go`) and, when
 * the remainder is too short, sends no `trial_end` at all rather than a rejected one. So the
 * remaining time is not shortened or refunded: it is simply dropped, and the new plan is
 * charged in full the moment the checkout completes.
 *
 * Strict, like the server's `until.After(now.Add(MinTrialPeriod))` — exactly 48 hours out is
 * already too short.
 */
const MIN_CARRY_OVER_MS = 48 * 60 * 60 * 1000;

/** Where a remainder came from, which decides what it is called on screen. */
type RemainderSource = 'paid' | 'trial';

/**
 * Only these two payment states leave anything behind — `purchase.carriedOver` in the People
 * service's `internal/app/payments/captain.go`. A plan that is past due, unpaid or paused is
 * a period Stripe never collected, so it was never bought and there is nothing to carry.
 */
const REMAINDER_SOURCE: Partial<Record<PaymentStatus, RemainderSource>> = {
  [PaymentStatus.ACTIVE]: 'paid',
  [PaymentStatus.TRIALING]: 'trial',
};

export type PlanRemainder = {
  /** When the current plan runs out, as an epoch ms. */
  until: number;
  source: RemainderSource;
  /** Whether it is long enough to survive the takeover — see `MIN_CARRY_OVER_MS`. */
  isCarried: boolean;
};

/**
 * What the plan being taken over still has to run, and whether the owner keeps it.
 *
 * `null` means there is nothing to carry either way, so neither promise nor warning applies.
 *
 * The server reads a trialing plan's remainder off `TrialEndsAt`, which is `json:"-"` and so
 * never reaches this client. `next_payment_at` stands in for it: it is the subscription
 * item's `current_period_end`, and for a trialing Stripe subscription that is the trial end.
 * `cancel_at` is deliberately not consulted even when the plan is winding down — the server
 * carries over the period end whatever the payer scheduled, and this has to say what the
 * server will do rather than what the vessel's own header shows.
 */
export const remainderOnTakeover = (
  subscription: Subscription | null,
  now: number = Date.now(),
): PlanRemainder | null => {
  if (!isPaidPlan(subscription) || !subscription.paymentStatus) return null;

  const source = REMAINDER_SOURCE[subscription.paymentStatus];
  const until = timestamp(subscription.nextPaymentAt);
  if (!source || until === null) return null;

  return { until, source, isCarried: until > now + MIN_CARRY_OVER_MS };
};
